import type { ReactNode } from 'react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { cn } from 'utils/shadcn';

import {
  buildLoadBuildingLocationSection,
  hasLoadBuildingLocationContent,
} from '../utils/formatLoadBuildingPreview';

const previewLineClass =
  'text-[11px] text-neutral-600 wrap-break-word leading-tight';

export type SuggestionPreviewField = {
  key?: string;
  label: string;
  value: string;
};

type SuggestionPreviewSectionProps = {
  label?: string;
  className?: string;
  // Flat label:value rows (default layout). Ignored when isLoadBuildingAddress is set.
  fields?: SuggestionPreviewField[];
  // Load Building addresses are formatted differently:
  // - First we show the address header block without labels, then scheduling fields, then the rest
  isLoadBuildingAddress?: boolean;
  sectionKey?: string;
  flattenedChanges?: Record<string, unknown>;
  tmsName?: TMS;
};

// Category header + nested content with left rule (matches Kit suggestion categories).
export function SuggestionPreviewSection({
  label,
  className,
  fields,
  isLoadBuildingAddress = false,
  sectionKey,
  flattenedChanges,
  tmsName,
}: SuggestionPreviewSectionProps) {
  let content: Maybe<ReactNode> = null;

  if (isLoadBuildingAddress) {
    if (sectionKey && flattenedChanges && tmsName) {
      content = buildLoadBuildingAddressLayout(
        sectionKey,
        flattenedChanges,
        tmsName
      );
    }
  } else if (fields && fields.length > 0) {
    content = <SuggestionPreviewFieldsFlat fields={fields} />;
  }

  if (!content) {
    return null;
  }

  return (
    <Flex
      direction='col'
      gap='none'
      className={cn('gap-y-0.5 min-w-0', className)}
    >
      {label ? (
        <Typography
          variant='body-xs'
          weight='semibold'
          className='text-neutral-600 text-[11px] mt-0.5'
        >
          {label}
        </Typography>
      ) : null}
      <Flex
        direction='col'
        gap='none'
        className={cn(
          'gap-y-0.5',
          label ? 'ml-0.5 border-l border-neutral-300 pl-2' : 'ml-1.5'
        )}
      >
        {content}
      </Flex>
    </Flex>
  );
}

// Renders flat "label: value" preview rows.
function SuggestionPreviewFieldsFlat({
  fields,
}: {
  fields: SuggestionPreviewField[];
}) {
  return fields.map((field, idx) => (
    <Typography
      key={field.key ?? `${field.label}-${idx}`}
      as='div'
      variant='body-xs'
      textColor='lessMuted'
      className={previewLineClass}
    >
      <Typography
        as='span'
        variant='body-xs'
        weight='medium'
        textColor='lessMuted'
        className={previewLineClass}
      >
        {`${field.label}: `}
      </Typography>
      <Typography
        as='span'
        variant='body-xs'
        textColor='lessMuted'
        className={previewLineClass}
      >
        {field.value}
      </Typography>
    </Typography>
  ));
}

// Spaced group within address layout (header / scheduling / other).
function SuggestionPreviewContentBlock({
  children,
  spacedAfter = false,
}: {
  children: ReactNode;
  spacedAfter?: boolean;
}) {
  return (
    <Flex
      direction='col'
      gap='none'
      className={cn('gap-y-0.5', spacedAfter && 'mb-2')}
    >
      {children}
    </Flex>
  );
}

// Builds Load Building blocks: location header, then scheduling, then remaining fields.
function buildLoadBuildingAddressLayout(
  sectionKey: string,
  flattenedChanges: Record<string, unknown>,
  tmsName: TMS
): Maybe<ReactNode> {
  const preview = buildLoadBuildingLocationSection(
    sectionKey,
    flattenedChanges,
    tmsName
  );

  if (!hasLoadBuildingLocationContent(preview)) {
    return null;
  }

  const blocks: ReactNode[] = [];

  if (preview.headerLines.length > 0) {
    blocks.push(
      preview.headerLines.map((line, idx) => (
        <div key={`header-${idx}`} className={previewLineClass}>
          {line}
        </div>
      ))
    );
  }

  if (preview.schedulingFields.length > 0) {
    blocks.push(
      <SuggestionPreviewFieldsFlat fields={preview.schedulingFields} />
    );
  }

  if (preview.bottomFields.length > 0) {
    blocks.push(<SuggestionPreviewFieldsFlat fields={preview.bottomFields} />);
  }

  return blocks.map((block, idx) => (
    <SuggestionPreviewContentBlock
      key={idx}
      spacedAfter={idx < blocks.length - 1}
    >
      {block}
    </SuggestionPreviewContentBlock>
  ));
}
